import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

/**
 * Card — container "flutuante" no papel.
 *
 * Composição:
 *   <Card>
 *     <CardHeader>
 *       <CardTitle>Negócios em aberto</CardTitle>
 *       <CardDescription>15 negócios somando R$ 487 mil</CardDescription>
 *     </CardHeader>
 *     <CardContent>...</CardContent>
 *     <CardFooter>...</CardFooter>
 *   </Card>
 *
 * Variants:
 *   - default: fundo branco/escuro + sombra sutil + rounded-2xl
 *   - urgent: rail vermelho lateral à esquerda (3px)
 *   - flat: sem sombra, apenas borda
 */

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'urgent' | 'flat';
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ variant = 'default', className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'bg-paper-surface dark:bg-paper-dark-surface rounded-2xl',
        variant === 'default' && 'shadow-card',
        variant === 'urgent' && 'shadow-card relative pl-4 before:absolute before:left-0 before:top-3 before:bottom-3 before:w-[3px] before:bg-red before:rounded-r',
        variant === 'flat' && 'border border-line dark:border-line-dark',
        className
      )}
      {...props}
    />
  )
);
Card.displayName = 'Card';

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pb-3', className)} {...props} />
  )
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      // FASE-B (condição 3 da aprovação): título de card = Inter 600 —
      // Oswald fica reservado pra display e títulos de página/seção (≥20px).
      className={cn(
        'text-base font-semibold text-ink-1 dark:text-ink-dark-1 leading-tight',
        className
      )}
      {...props}
    />
  )
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn('text-sm text-ink-2 dark:text-ink-dark-2 mt-1', className)}
      {...props}
    />
  )
);
CardDescription.displayName = 'CardDescription';

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-3', className)} {...props} />
  )
);
CardContent.displayName = 'CardContent';

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'p-6 pt-3 flex items-center gap-2 border-t border-line-soft dark:border-line-soft-dark',
        className
      )}
      {...props}
    />
  )
);
CardFooter.displayName = 'CardFooter';
